import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Info, User, Mail,
  Eye, EyeOff
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { SCREENS } from '../utils/constants';
import Logo from '../icons/logo.svg';


// Stage Content Component
const StageContent = React.memo(({
  stage,
  stageConfig,
  userData,
  formErrors,
  isDarkMode,
  inputRefs,
  showPassword,
  setShowPassword,
  handleInputChange,
  goToNextStage,
  isProcessing,
  handleCompletionAnimation,
  t
}) => {
  const currentConfig = stageConfig[stage];

  // Render logic based on stage
  const renderInput = () => {
    const inputClasses = `w-full px-5 py-4 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all border ${isDarkMode
      ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800/80'
      : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400 focus:bg-white'
      }`;

    switch (stage) {
      case 'name':
        return (
          <div className="space-y-2">
            <input
              ref={(el) => (inputRefs.current.name = el)}
              type="text"
              value={userData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('signup.namePlaceholder', 'Your full name')}
              className={inputClasses}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && goToNextStage()}
            />
          </div>
        );
      case 'email':
        return (
          <div className="space-y-2">
            <input
              ref={(el) => (inputRefs.current.email = el)}
              type="email"
              value={userData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={t('signup.emailPlaceholder', 'name@example.com')}
              className={inputClasses}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && goToNextStage()}
            />
          </div>
        );
      case 'password':
        return (
          <div className="space-y-4">
            <div className="relative group">
              <input
                ref={(el) => (inputRefs.current.password = el)}
                type={showPassword ? "text" : "password"}
                value={userData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder={t('signup.passwordPlaceholder', 'Minimum 8 characters')}
                className={inputClasses}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && goToNextStage()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 transition-colors p-2"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        );
      case 'confirm':
        return (
          <div className="space-y-6">
            <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-slate-800/50' : 'bg-blue-50'} space-y-3`}>
              <div className="flex items-center gap-3">
                <User size={18} className="text-blue-500" />
                <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{userData.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-blue-500" />
                <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{userData.email}</span>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (!currentConfig) return null;

  return (
    <div className="w-full">
      {/* Input Area */}
      <div className="mb-6">
        {renderInput()}
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {formErrors[stage] && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className="flex items-center gap-2 text-red-500 text-sm overflow-hidden"
          >
            <Info size={16} />
            <span>{formErrors[stage]}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={stage === 'confirm' ? handleCompletionAnimation : goToNextStage}
        disabled={isProcessing}
        className={`w-full py-4 rounded-full font-bold text-lg shadow-xl shadow-blue-500/20 relative overflow-hidden group ${isDarkMode
          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30 hover:shadow-blue-900/50'
          : 'bg-slate-900 text-white shadow-slate-200 hover:bg-slate-800'
          }`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {t('common.processing', 'Processing...')}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            {stage === 'confirm' ? t('signup.complete', 'Create Account') : t('common.continue', 'Continue')}
            <ChevronRight size={20} />
          </span>
        )}
      </motion.button>
    </div>
  );
});


const SignupScreen = () => {
  const { navigateToScreen, isDarkMode } = useUI();
  const { register } = useAuth();
  const { t } = useTranslation();

  // State Management
  const [signupStage, setSignupStage] = useState('name');
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const inputRefs = useRef({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  // Styles
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  // Orb Logic
  const { orbHue, orbScale, orbPulse } = useMemo(() => {
    let hue = 220; // Default Blue
    let scale = 1;
    let pulse = false;

    // Error State (Red)
    if (formErrors[signupStage]) {
      hue = 0;
      pulse = true;
      scale = 1.1;
    } else {
      // Check validity for Green State
      const currentVal = userData[signupStage] || '';
      let isValid = false;
      if (signupStage === 'email' && /\S+@\S+\.\S+/.test(currentVal)) isValid = true;
      if (signupStage === 'password' && currentVal.length >= 8) isValid = true;
      if (signupStage === 'name' && currentVal.length > 2) isValid = true;

      if (isValid) {
        hue = 150; // Green
        scale = 1.2; // Pop
      }
    }

    // Dynamic Growth logic (if not error)
    if (!pulse) {
      const currentLength = userData[signupStage]?.length || 0;
      // Base growth: start at 1, add 0.01 per char, cap at 1.15 + validity bonus (Subtle Aura)
      const growth = Math.min(currentLength * 0.01, 0.15);
      scale += growth;
    }

    return { orbHue: hue, orbScale: scale, orbPulse: pulse };
  }, [formErrors, signupStage, userData]);


  // Helper Functions
  const handleInputChange = useCallback((field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [formErrors]);

  const validateStage = useCallback(() => {
    const errors = {};
    if (signupStage === 'name' && !userData.name.trim()) {
      errors.name = t('signup.error.nameRequired', 'Name is required');
    }
    if (signupStage === 'email') {
      if (!userData.email.trim()) {
        errors.email = t('signup.error.emailRequired', 'Email is required');
      } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
        errors.email = t('signup.error.emailInvalid', 'Invalid email address');
      }
    }
    if (signupStage === 'password' && userData.password.length < 8) {
      errors.password = t('signup.error.passwordLength', 'Password must be at least 8 characters');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [signupStage, userData, t]);

  const goToNextStage = useCallback(() => {
    if (!validateStage()) return;

    if (signupStage === 'name') setSignupStage('email');
    else if (signupStage === 'email') setSignupStage('password');
    else if (signupStage === 'password') setSignupStage('confirm');
  }, [signupStage, validateStage]);

  const goToPrevStage = useCallback(() => {
    if (signupStage === 'email') setSignupStage('name');
    else if (signupStage === 'password') setSignupStage('email');
    else if (signupStage === 'confirm') setSignupStage('password');
  }, [signupStage]);

  const handleCompletionAnimation = async () => {
    setIsProcessing(true);
    try {
      // Implement actual registration logic here
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulating API call
      // await register(userData);
      navigateToScreen(SCREENS.ONBOARDING);
    } catch (error) {
      console.error("Registration failed", error);
      setFormErrors({ confirm: t('signup.error.generic', 'Registration failed. Please try again.') });
    } finally {
      setIsProcessing(false);
    }
  };

  // Stage Config for Titles
  const stageConfig = {
    name: { title: t('signup.nameTitle', "What's your name?"), subtitle: t('signup.nameSubtitle', "This will be displayed on your profile") },
    email: { title: t('signup.emailTitle', "What's your email?"), subtitle: t('signup.emailSubtitle', "We'll use this for account verification") },
    password: { title: t('signup.passwordTitle', "Create a password"), subtitle: t('signup.passwordSubtitle', "Make it strong and unique") },
    confirm: { title: t('signup.confirmTitle', "Confirm details"), subtitle: t('signup.confirmSubtitle', "Make sure everything is correct") }
  };



  return (
    <motion.div
      className={`w-full min-h-screen flex flex-col items-center justify-center ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => {
          if (signupStage === 'name') {
            navigateToScreen(SCREENS.WELCOME);
          } else {
            goToPrevStage();
          }
        }}
        className="absolute top-6 left-6 z-20 p-2.5 rounded-full hover:bg-white/10 transition-colors backdrop-blur-sm"
        aria-label="Back"
      >
        <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
      </motion.button>

      {/* Main Content Card */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 md:p-10 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header Section with Interactive Logo Aura */}
        <div className="flex flex-col items-center mb-8 relative z-20">

          {/* Logo & Dynamic Aura Container */}
          <div className="relative mb-8 flex items-center justify-center">

            {/* Dynamic Aura (Replacing the Orb) */}
            <motion.div
              animate={{
                background: formErrors[signupStage]
                  ? `radial-gradient(circle, rgba(239, 68, 68, 0.6) 0%, rgba(239, 68, 68, 0) 70%)` // Red Error
                  : orbHue === 150
                    ? `radial-gradient(circle, rgba(34, 197, 94, 0.6) 0%, rgba(34, 197, 94, 0) 70%)` // Green Success
                    : `radial-gradient(circle, rgba(59, 130, 246, 0.5) 0%, rgba(99, 102, 241, 0.2) 70%)`, // Blue/Indigo Neutral,
                scale: orbPulse ? [orbScale, orbScale * 1.1, orbScale] : orbScale,
                opacity: 0.8 + (orbScale - 1) // Increase opacity slightly with input
              }}
              transition={{ duration: 0.5 }}
              className={`absolute inset-[-40px] rounded-full blur-[50px] z-0`}
            />

            {/* Rotating Subtle Glow for Idle State */}
            {!formErrors[signupStage] && orbHue !== 150 && (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-20px] rounded-full opacity-30 z-0 bg-gradient-to-tr from-blue-500 to-purple-500 blur-[30px]"
              />
            )}

            {/* Large Welcome-Style Logo */}
            <motion.img
              src={Logo}
              alt="IDApps"
              animate={{
                scale: orbPulse ? 1.05 : 1
              }}
              className="w-32 h-32 object-contain drop-shadow-2xl relative z-10"
              style={{ filter: isDarkMode ? 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.5))' : 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3))' }}
            />
          </div>

          <h2 className="relative z-10 text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600 text-center">
            {stageConfig[signupStage]?.title}
          </h2>
          <p className={`relative z-10 text-base mt-2 text-center max-w-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {stageConfig[signupStage]?.subtitle}
          </p>
        </div>

        {/* Dynamic Form Content */}
        <StageContent
          stage={signupStage}
          stageConfig={stageConfig}
          userData={userData}
          formErrors={formErrors}
          isDarkMode={isDarkMode}
          inputRefs={inputRefs}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleInputChange={handleInputChange}
          goToPrevStage={goToPrevStage}
          goToNextStage={goToNextStage}
          isProcessing={isProcessing}
          handleCompletionAnimation={handleCompletionAnimation}
          t={t}
        />

        {/* Footer Link */}
        <div className="mt-8 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('signup.haveAccount', "Already have an account? ")}
            <button
              onClick={() => navigateToScreen(SCREENS.LOGIN)}
              className="font-bold text-blue-500 hover:text-blue-400 transition-colors ml-1"
            >
              {t('signup.login', 'Sign In')}
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SignupScreen;